import React, { useMemo } from "react";
import { RemnatsProps } from "../api/ProductRemnants";
import { StyledTable, SmallTd90, SmallTd, TableWrap } from "./RemnantsStyled";
import { RemnantRowProps, DisabledSend } from "./common";
import useSelectCell from "./useSelectCell";
import FullRemnantsHeader from "./FullRemnantsHeader";
import { useColumnControl } from "./useColumnControl";
import { useSortableData } from "./useSortableData";
import FullRemnantsItem from "./FullRemnantsItem";
import { usePaginator } from "../shared/Paginator";
import { usePageSize } from "./usePagination";
import { useFilter, NeedUpdateFilterValue } from "./useFilter";
import type { RemnantsSendingFlags } from "./index";

function needUpdateMatches(
  flag: boolean | null | undefined,
  filter: NeedUpdateFilterValue
): boolean {
  if (filter === "all") return true;
  if (filter === "need") return flag === true;
  return flag !== true;
}

const FullRemnants = (props: {
  remnants: RemnantRowProps[];
  update: (
    props: {
      article: string;
      value: Partial<RemnatsProps["product_remnants"]>;
    }[]
  ) => Promise<boolean>;
  updateDisabledSend: (
    props: {
      article: string;
      value: Partial<DisabledSend>;
    }[]
  ) => Promise<boolean>;
  reloadRemnants: () => Promise<void>;
  updateMpNeedUpdate: (
    rows: {
      article: string;
      wb_data?: { need_update_stock: boolean };
      ozon_data?: { need_update_stock: boolean };
      ya_data?: { need_update_stock: boolean };
      sber_data?: { need_update_stock: boolean };
      ali_data?: { need_update_stock: boolean };
    }[]
  ) => Promise<boolean>;
  remnantsSendingFlags: RemnantsSendingFlags;
  retirementEnabled: boolean;
  onRetire: (
    article: string,
    marketplace: "wildberries" | "ozon",
    action: "retire" | "return"
  ) => Promise<boolean>;
}) => {
  const columnControl = useColumnControl();
  const filters = useFilter();

  const remnants = useMemo(
    () =>
      props.remnants.filter((el) => {
        if (
          !(
            (filters.showCombo && el.is_combo) ||
            (filters.showSingle && !el.is_combo)
          )
        )
          return false;
        if (
          filters.search &&
          !el.article.toLowerCase().includes(filters.search.toLowerCase())
        )
          return false;
        if (
          filters.blockFilterWb !== "all" &&
          (el.disabled_send_to_wildberries ?? false) !==
            (filters.blockFilterWb === "blocked")
        )
          return false;
        if (
          filters.blockFilterOzon !== "all" &&
          (el.disabled_send_to_ozon ?? false) !==
            (filters.blockFilterOzon === "blocked")
        )
          return false;
        if (
          filters.blockFilterYa !== "all" &&
          (el.disabled_send_to_ya ?? false) !== (filters.blockFilterYa === "blocked")
        )
          return false;
        if (
          filters.blockFilterSber !== "all" &&
          (el.disabled_send_to_sber ?? false) !==
            (filters.blockFilterSber === "blocked")
        )
          return false;
        if (
          filters.blockFilterAli !== "all" &&
          (el.disabled_send_to_ali ?? false) !==
            (filters.blockFilterAli === "blocked")
        )
          return false;
        if (filters.ozonQuarantineFilter !== "all") {
          const quarantined = Boolean(el.ozon_quarantine_at);
          if (
            quarantined !==
            (filters.ozonQuarantineFilter === "quarantine")
          )
            return false;
        }
        if (
          !needUpdateMatches(
            el.wb_data?.need_update_stock,
            filters.needUpdateFilterWb
          )
        )
          return false;
        if (
          !needUpdateMatches(
            el.ozon_data?.need_update_stock,
            filters.needUpdateFilterOzon
          )
        )
          return false;
        if (
          !needUpdateMatches(el.ya_data?.need_update_stock, filters.needUpdateFilterYa)
        )
          return false;
        if (
          !needUpdateMatches(
            el.sber_data?.need_update_stock,
            filters.needUpdateFilterSber
          )
        )
          return false;
        if (
          !needUpdateMatches(
            el.ali_data?.need_update_stock,
            filters.needUpdateFilterAli
          )
        )
          return false;
        if (filters.sendAgeFilter !== "all") {
          const tsKey =
            filters.sendAgeMp === "wb"
              ? el.last_send_wb_at
              : filters.sendAgeMp === "ozon"
              ? el.last_send_ozon_at
              : filters.sendAgeMp === "ya"
              ? el.last_send_ya_at
              : filters.sendAgeMp === "sber"
              ? el.last_send_sber_at
              : el.last_send_ali_at;
          const now = Date.now();
          if (filters.sendAgeFilter === "never") {
            if (tsKey != null) return false;
          } else if (tsKey == null) {
            return false;
          } else if (filters.sendAgeFilter === "hour") {
            if (now - tsKey > 60 * 60 * 1000) return false;
          } else if (filters.sendAgeFilter === "day") {
            if (now - tsKey > 24 * 60 * 60 * 1000) return false;
          } else if (filters.sendAgeFilter === "older") {
            if (now - tsKey <= 24 * 60 * 60 * 1000) return false;
          }
        }
        return true;
      }),
    [filters, props.remnants]
  );
  const { items, toggleSort, setSort, sortConfig, clearSort } =
    useSortableData(remnants);
  const { pageSize, setPageSize } = usePageSize();
  const paginator = usePaginator(items, pageSize);
  const { onClick, cells } = useSelectCell(paginator.pageItems);

  const update = async (propsLocal: {
    article: string;
    value: Partial<RemnatsProps["product_remnants"]>;
  }) => {
    const keys = Object.keys(propsLocal.value);
    if (!keys.length) return false;
    const value = (propsLocal.value as any)[keys[0]] as any;
    const result = cells.reduce((acc, el) => {
      return {
        ...acc,
        [el.row]: {
          article: el.row,
          value: {
            ...(acc[el.row]?.value || {}),
            ...((el.column === "wb" && { share_in_wildberries: value }) ||
              (el.column === "ozon" && { share_in_ozon: value }) ||
              (el.column === "ya" && { share_in_ya: value }) ||
              (el.column === "sber" && { share_in_sber: value }) ||
              (el.column === "ali" && { share_in_ali: value }) ||
              (el.column === "combo" && { share_in_combo: value }) ||
              {}),
          },
        },
      };
    }, {} as any);

    return props.update(
      cells.length ? [...(Object.values(result as any) as any)] : [propsLocal]
    );
  };
  return (
    <TableWrap>
      <StyledTable striped bordered hover onClick={onClick}>
        <FullRemnantsHeader
          columnControl={columnControl}
          toggleSort={toggleSort}
          setSort={setSort}
          clearSort={clearSort}
          sortConfig={sortConfig}
          paginator={paginator}
          pageSize={pageSize}
          setPageSize={setPageSize}
          filters={filters}
          filteredRowCount={items.length}
        />
        <tbody>
          {paginator.pageItems.map((el) => (
            <FullRemnantsItem
              key={el.article}
              data={el}
              remnants={props.remnants}
              columnControl={columnControl}
              update={update}
              hoverCells={cells}
              updateDisabledSend={props.updateDisabledSend}
              updateMpNeedUpdate={props.updateMpNeedUpdate}
              remnantsSendingFlags={props.remnantsSendingFlags}
              retirementEnabled={props.retirementEnabled}
              onRetire={props.onRetire}
            />
          ))}
        </tbody>
      </StyledTable>
    </TableWrap>
  );
};

export default FullRemnants;
